import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import LabelWrapper from "components/Common/LabelWrapper";
import CountUp from "react-countup";
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import {
  COLOR_CONTENT_CREATED_TYPE,
  COLOR_CONTENT_STATUS,
  ENUM_CONTENT_CREATED_TYPE,
  ENUM_CONTENT_STATUS,
  ENUM_CONTENT_TYPE,
  LIST_CONTENT_CREATED_TYPE_OPTION,
  LIST_CONTENT_STATUS_OPTION,
  STATUS_ACTIVE_DISABLE_OPTION,
} from "helpers/constans";
import {
  formatDateString,
  formatDateStringToKorea,
  formatDateStringToUTC,
  formatNumberWithCommas,
} from "helpers/format";
import CopyWrapper from "components/Common/CopyWrapper";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import { getContents, updateContent } from "store/content/thunk";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import noImageJob from "../../../assets/images/no-job-post.png";
import NO_IMAGE_USER from "../../../assets/images/no-user.png";
import ContentForm from "./ContentForm";
import ModalConfirm from "components/Common/ModalConfirm";
import contentApi from "api/contentApi";
import { isHavePermissionRole, ROLES_FOR_APP } from "helpers/role";
import { useRole } from "components/Hooks/UserHooks";
import ModalPreview from "components/Common/ModalPreview";
import ModalMultiPreviewImage from "components/Common/ModalMultiPreviewImage";
import CollapseContent from "components/Common/CollapseContent";
import TooltipCustom from "components/Common/TooltipCustom";
import { Link } from "react-router-dom";
import CommentDetailModal from "./CommentDetailModal";

registerLocale("en", en);
registerLocale("ko", ko);

const ContentList = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();
  const dispatch: any = useDispatch();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    nickname: withDefault(StringParam, ""),
    storeName: withDefault(StringParam, ""),
    email: withDefault(StringParam, ""),
    contents: withDefault(StringParam, ""),
    postTypeId: withDefault(StringParam, ""),
    createdType: withDefault(StringParam, ""),
    status: withDefault(StringParam, ""),
    generatedByAI: withDefault(NumberParam, 0),
    startDate: withDefault(StringParam, ""),
    endDate: withDefault(StringParam, ""),
  });

  const [nickNameSearch, setNickNameSearch] = useState<string>(
    query?.nickname || ""
  );
  const [storeNameSearch, setStoreNameSearch] = useState<string>(
    query?.storeName || ""
  );
  const [emailSearch, setEmailSearch] = useState<string>(query?.email || "");
  const [contentSearch, setContentSearch] = useState<string>(
    query?.contents || ""
  );
  const [contentTypeSearch, setContentTypeSearch] = useState<string>(
    query?.postTypeId || ""
  );
  const [createdTypeSearch, setCreatedTypeSearch] = useState<string>(
    query?.createdType || ""
  );
  const [statusSearch, setStatusSearch] = useState<any>(
    STATUS_ACTIVE_DISABLE_OPTION?.find(
      (item) => item?.value === parseInt(query?.status)
    )?.value ?? ""
  );

  const [dateSearch, setDateSearch] = useState<any[]>([
    query?.startDate
      ? moment(query?.startDate || "", "Y-MM-DD").toDate()
      : null,
    query?.endDate ? moment(query?.endDate || "", "Y-MM-DD").toDate() : null,
  ]);
  const [startDate, endDate] = dateSearch;

  const [linkImage, setLinkImage] = useState<any>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [itemToEdit, setItemToEdit] = useState<any>(false);
  const [dataRatingDefinition, setDataRatingDefinition] = useState<any>([]);
  const [dataContentType, setDataContentType] = useState<any>([]);
  const [itemToCommentList, setItemToCommentList] = useState<any>(null);

  const selectLayoutState = (state: any) => state.Content;

  const ContentProperties = createSelector(selectLayoutState, (state) => ({
    contents: state.contents,
    isContentLoading: state.isContentLoading,
    isContentSuccess: state.isContentSuccess,

    error: state.error,
  }));

  const { contents, isContentLoading } = useSelector(ContentProperties);

  function loadTableData() {
    dispatch(getContents(query));
  }

  useEffect(() => {
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      nickname: nickNameSearch.trim() || "",
      storeName: storeNameSearch.trim() || "",
      email: emailSearch.trim() || "",
      contents: contentSearch.trim() || "",
      postTypeId: contentTypeSearch || "",
      createdType: createdTypeSearch || "",
      status: statusSearch,
      startDate: startDate
        ? formatDateStringToUTC(
          moment(new Date(startDate))
            .startOf("day")
            .format("Y-MM-DD HH:mm:ss")
        )
        : "",
      endDate: endDate
        ? formatDateStringToUTC(
          moment(new Date(endDate)).endOf("day").format("Y-MM-DD HH:mm:ss")
        )
        : "",
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        nickname: "",
        storeName: "",
        email: "",
        contents: "",
        postTypeId: "",
        createdType: "",
        status: "",
        startDate: "",
        endDate: "",
        sortBy: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setNickNameSearch("");
    setStoreNameSearch("");
    setEmailSearch("");
    setContentSearch("");
    setContentTypeSearch("");
    setCreatedTypeSearch("");
    setStatusSearch("");
    setDateSearch([null, null]);
  };

  const handleOnConfirmUpdate = async (item: {
    data: any;
    status: string | boolean;
  }) => {
    if (!item || !item?.data?.id) return;

    try {
      const formatData = {
        status: item?.status,
      };
      const response: any = await updateContent(item?.data?.id, formatData);
      if (response?.code === 200) {
        loadTableData();
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div style={{ width: 35 }}>
              {contents?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Community Page"),
        accessor: "lp",
        filterable: false,
        sortable: false,
        thWidth: i18n?.language === "en" ? 200 : 160,
        thClass: "text-center whitespace-nowrap",
        thComponent: () => (
          <div className="position-relative">
            <div>{t("Community Page")}</div>
            <TooltipCustom
              title={t(
                "This is the community page link for marketing purposes."
              )}
              className="d-inline-block vertical-align-middle ms-1 thumbnail-mt"
              style={{
                transform: "translateY(2px)",
                position: "absolute",
                top: -1,
                right: 0,
                paddingLeft: 4,
                paddingRight: 3,
              }}
            >
              <i
                className="ri-question-line align-bottom text-secondary"
                style={{ fontSize: "16px" }}
              ></i>
            </TooltipCustom>
          </div>
        ),
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          const id = item?.id;
          const type = item?.type;
          const status = item?.status;
          return type === ENUM_CONTENT_TYPE.COMMUNITY &&
            status === ENUM_CONTENT_STATUS.ACTIVE ? (
            <div className="text-center" style={{ minWidth: 80 }}>
              <Link
                target="_blank"
                to={`https://community.onnyjob.kr/${id}`}
                className="text-decoration-underline"
              >
                {t("Get Link")}
              </Link>
            </div>
          ) : (
            <div className="text-center" style={{ minWidth: 80 }}>
              -
            </div>
          );
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        filterable: false,
        sortable: false,
        thWidth: 100,
        thClass: "text-center",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <div
              className="form-check form-switch form-switch-sm text-center"
              dir="ltr"
              style={{ minWidth: "80px" }}
            >
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={
                  cell?.value === ENUM_CONTENT_STATUS.ACTIVE ? true : false
                }
                onChange={(e) => {
                  const value = e?.target?.checked;
                  handleOnConfirmUpdate({
                    data: item,
                    status:
                      value === true
                        ? ENUM_CONTENT_STATUS.ACTIVE
                        : ENUM_CONTENT_STATUS.INACTIVE,
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Photo"),
        accessor: "files",
        thClass: "text-center whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 120,
        Cell: (cell: any) => {
          const itemImages = cell?.value;
          const jobImage = itemImages?.[0]?.url || "";
          const formatPreviewImages =
            itemImages?.length > 0 &&
            itemImages?.map((image: any) => image?.url);

          return (
            <>
              <div className="w-100 text-center " style={{ minWidth: "80px" }}>
                <div
                  style={{ width: "80px" }}
                  className="m-auto cursor-pointer"
                  onClick={() =>
                    setLinkImage(
                      itemImages?.length > 1
                        ? { urls: formatPreviewImages }
                        : { url: jobImage }
                    )
                  }
                >
                  <img
                    src={jobImage ?? noImageJob}
                    width={70}
                    height={70}
                    className=""
                    style={{ objectFit: "contain" }}
                    alt="thumbnail"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = noImageJob;
                    }}
                  />
                </div>
              </div>
            </>
          );
        },
      },
      {
        Header: t("Content Type"),
        accessor: "postType.name",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const typeName = cell?.value;
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              { typeName }
            </div>
          );
        },
      },
      {
        Header: t("Created Type"),
        accessor: "createdType",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const status = cell?.value;
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_CONTENT_CREATED_TYPE[status] || "secondary"
                  }`}
              >
                {t(
                  LIST_CONTENT_CREATED_TYPE_OPTION?.find(
                    (item: any) => item?.value === status
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Title"),
        accessor: "title",
        filterable: true,
        sortable: false,
        thWidth: 240,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const title = cell?.value;
          const item = cell?.row?.original;

          return title ? (
            <div 
              className="cursor-pointer text-primary"
              onClick={() => {
                setIsOpen(true);
                setItemToEdit(item);
              }} 
              >
              <CopyWrapper contentCopy={title} style={{ minWidth: "220px" }}>
                <CollapseContent
                  style={{ maxWidth: "180px" }}
                  dataOriginal={title}
                  isRank={false}
                  isInlineBlock={true}
                  isShowModal={true}
                  titleModal={t("Title")}
                  numberCharactor={50}
                />
              </CopyWrapper>
            </div>
          ) : (
            ""
          );
        },
      },
      {
        Header: t("Content"),
        accessor: "contents",
        filterable: true,
        sortable: false,
        thWidth: 280,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const content = cell?.value;
          return content ? (
            <CopyWrapper contentCopy={content} style={{ minWidth: "260px" }}>
              <CollapseContent
                style={{ maxWidth: "350px" }}
                dataOriginal={content}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Content")}
              />
            </CopyWrapper>
          ) : (
            ""
          );
        },
      },
      {
        Header: t("Store Name"),
        accessor: "storeName",
        filterable: true,
        sortable: false,
        thWidth: 200,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return cell?.value ? (
            <div className="cursor-pointer" style={{ width: "180px" }}>
              <CopyWrapper contentCopy={cell?.value}>
                {`${cell?.value}`}
              </CopyWrapper>
            </div>
          ) : (
            "-"
          );
        },
      },
      {
        Header: t("User"),
        accessor: "user",
        filterable: false,
        sortable: false,
        thWidth: 280,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          const user = cell?.value as any;
          const createdType = item?.createdType;

          return createdType === ENUM_CONTENT_CREATED_TYPE.NORMAL ? (
            <div className="d-flex align-items-center gap-2">
              <div
                className="cursor-pointer overflow-hidden object-fit-cover"
                style={{
                  height: "45px",
                  width: "45px",
                  minWidth: "45px",
                }}
                onClick={() =>
                  setLinkImage(
                    user?.profile?.imageUrls?.length > 1
                      ? { urls: user?.profile?.imageUrls }
                      : { url: user?.profile?.imageUrls?.[0] || "" }
                  )
                }
              >
                <img
                  className="overflow-hidden object-fit-cover rounded-circle"
                  src={user?.profile?.imageUrls?.[0] || NO_IMAGE_USER}
                  alt={user?.profile?.nickname}
                  height={45}
                  width={45}
                  style={{
                    minWidth: "45px",
                  }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = NO_IMAGE_USER;
                  }}
                />
              </div>
              <div>
                {
                  user?.profile ? (
                    <>
                      <div>{user?.profile?.nickname || user?.profile?.fullname || "-"}</div>
                      <div className="mt-1">
                        <small className="text-secondary">
                          {user?.profile?.email || ""}
                        </small>
                      </div>
                    </>
                  ) : (
                    <div style={{wordBreak: "break-word"}}>
                      {user?.email || "-"}
                    </div>
                  )
                }
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <div
                className="cursor-pointer overflow-hidden object-fit-cover"
                style={{
                  height: "45px",
                  width: "45px",
                  minWidth: "45px",
                }}
              >
                <img
                  className="overflow-hidden object-fit-cover rounded-circle"
                  src={item?.avatar || NO_IMAGE_USER}
                  alt={item?.nickname}
                  height={45}
                  width={45}
                  style={{
                    minWidth: "45px",
                  }}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = NO_IMAGE_USER;
                  }}
                />
              </div>
              <div>{item?.nickname}</div>
            </div>
          );
        },
      },
      {
        Header: t("UDID"),
        accessor: "user.devices",
        filterable: true,
        sortable: false,
        thWidth: 220,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const item: any = cell?.value?.[0];
          const udid = item?.udid;
          return udid ? (
            <div className="cursor-pointer" style={{ minWidth: "200px" }}>
              <CopyWrapper contentCopy={udid}>
                {`${udid}`}
              </CopyWrapper>
            </div>
          ) : "-";
        },
      },
      {
        Header: t("Views"),
        accessor: "pv",
        thClass: "align-bottom text-end",
        filterable: false,
        sortable: false,
        thWidth: 240,
        thComponent: () => (
          <table>
            <thead>
              <tr>
                <th className="text-center pb-1 none-sticky" colSpan={2}>
                  {t("Views")}
                </th>
              </tr>
              <tr>
                <th
                  className="text-center px-0 cursor-pointer none-sticky"
                  style={{ width: "120px" }}
                >
                  {t("PV")}
                </th>
                <th
                  className="text-center px-0 cursor-pointer none-sticky"
                  style={{ width: "120px" }}
                >
                  {t("UV")}
                </th>
              </tr>
            </thead>
          </table>
        ),
        Cell: (cell: any) => {
          const item: any = cell?.row?.original?.view;
          return (
            <div className="d-flex text-center">
              <div style={{ width: "120px" }}>
                {formatNumberWithCommas(item?.pv)}
              </div>
              <div style={{ width: "120px", borderLeft: "1px solid #eaeaea" }}>
                {formatNumberWithCommas(item?.uv)}
              </div>
            </div>
          );
        },
      },
      {
        Header: t("Total Like"),
        accessor: "likeCount",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap text-center",
        thWidth: 120,
        Cell: (cell: any) => {
          const totalLike = cell?.value;

          return (
            <div className="text-center">
              {totalLike ? formatNumberWithCommas(totalLike) : "0"}
            </div>
          );
        },
      },
      {
        Header: t("Total Comment"),
        accessor: "commentCount",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap text-center",
        thWidth: 140,
        Cell: (cell: any) => {
          const totaComment = cell?.value;
          const item: any = cell?.row?.original;

          return (
            <div 
              onClick={() => {
                setItemToCommentList(item);
              }}
              className="text-center text-primary cursor-pointer">
              {totaComment ? formatNumberWithCommas(totaComment) : 0}
            </div>
          );
        },
      },
      {
        Header: t("Total Shared"),
        accessor: "shareCount",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap text-center",
        thWidth: 120,
        Cell: (cell: any) => {
          const totaShared = cell?.value;

          return (
            <div className="text-center">
              {totaShared ? formatNumberWithCommas(totaShared) : 0}
            </div>
          );
        },
      },
      {
        Header: t("Ratings"),
        accessor: "ratings",
        thClass: "text-end",
        filterable: false,
        sortable: false,
        thWidth:
          dataRatingDefinition?.length > 0
            ? i18n?.language === "en"
              ? 160 * (dataRatingDefinition?.length - 1) + 300
              : 160 * dataRatingDefinition?.length
            : 150,
        ...(dataRatingDefinition?.length > 0 && {
          thComponent: () => (
            <table>
              <thead>
                <tr>
                  <th
                    className="text-center pb-1 none-sticky"
                    colSpan={dataRatingDefinition?.length || 1}
                  >
                    {t("Ratings")}
                  </th>
                </tr>
                <tr>
                  {dataRatingDefinition?.map((rating: any, index: number) => (
                    <th
                      className={`${dataRatingDefinition?.length - 1 === index
                          ? "text-start"
                          : "text-center"
                        } cursor-pointer none-sticky position-relative align-bottom`}
                      style={{
                        width: `${index === dataRatingDefinition?.length - 1 &&
                            i18n?.language === "en"
                            ? "300px"
                            : "160px"
                          }`,
                        padding: "0 10px",
                      }}
                      key={index}
                    >
                      {i18n?.language === "en"
                        ? rating.label.en
                        : rating.label.ko}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          ),
        }),
        Cell: (cell: any) => {
          const ratings: any = cell?.row?.original?.ratings;

          return dataRatingDefinition?.length > 0 ? (
            <div className="d-flex text-center">
              {dataRatingDefinition?.map((item: any, index: number) => {
                let value = "";
                if (item?.type === "stars") {
                  value = ratings?.find((r: any) => r.key === item?.key)?.value;
                } else if (item?.type === "boolean") {
                  const valueOption = ratings?.find(
                    (r: any) => r.key === item?.key
                  )?.value;
                  const options = item?.options || [];
                  const label = options?.find(
                    (opt: any) => opt?.value === valueOption
                  )?.label;
                  value = i18n?.language == "en" ? label?.en : label?.ko;
                }

                return (
                  <div
                    key={index}
                    style={{
                      width: `${index === dataRatingDefinition?.length - 1 &&
                          i18n?.language === "en"
                          ? "300px"
                          : "160px"
                        }`,
                      textAlign: `${index == dataRatingDefinition?.length - 1
                          ? "left"
                          : "center"
                        }`,
                      borderRight: `${index == dataRatingDefinition?.length - 1
                          ? "none"
                          : "1px solid #eaeaea"
                        }`,
                      padding: "0 10px",
                    }}
                  >
                    {value || "-"}
                  </div>
                );
              })}
            </div>
          ) : (
            <>-</>
          );
        },
      },
      {
        Header: t("Published at"),
        accessor: "publishedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const publishedAt: any = cell?.row?.original?.publishedAt;
          const date = formatDateString(publishedAt, "YYYY-MM-DD HH:mm:ss");
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              {
                date ? (
                  <span>
                    <span>{arrDate?.[0] || ""}</span> <br />{" "}
                    <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                  </span>
                ) : (
                  "-"
                )
              }
            </div>
          )
        },
      },
      {
        Header: t("Installed at"),
        accessor: "installedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const installedAt: any = cell?.row?.original?.user?.devices?.[0]?.device?.installedAt;
          const date = formatDateStringToKorea(installedAt);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Created at"),
        accessor: "createdAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updatedAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Action"),
        thClass: "text-center",
        thWidth: 110,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "70px" }}
            >
              <TooltipCustom
                title={t("Update Content")}
                id={`update-us-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    setIsOpen(true);
                    setItemToEdit(item);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom"></i>
                </Link>
              </TooltipCustom>

              <TooltipCustom
                title={t("Delete Content")}
                id={`detail-cp-${item?.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-danger edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    setItemToDelete(item);
                  }}
                >
                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                </Link>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      i18n?.language,
      contents?.pagination?.total,
      query?.page,
      dataRatingDefinition,
    ]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  const handleActionDelete = async (id: any) => {
    if (!id) return;

    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await contentApi.deleteContent(id);
      if (response?.code) {
        setIsConfirmLoading((_prev) => false);
        loadTableData();
        setItemToDelete(false);
        toast(
          `${t("The content has been deleted.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseClick = () => {
    setItemToEdit((_prev: any) => null);
    setIsOpen((_prev) => false);
  };

  const onCloseCommentListClick = () => {
    setItemToCommentList((_prev: any) => null);
    loadTableData();
  };

  const triggerRefresh = () => {
    onCloseClick();
    loadTableData();
  };

  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  useEffect(() => {
    const handleCallAllOption = async () => {
      try {
        const [ratingDefinitions, contentTypes]: any = await Promise.all([
          contentApi.ratingDefinitions(),
          contentApi.contentTypes({limit: 1000})
        ]);

        if (ratingDefinitions?.data) {
          setDataRatingDefinition(ratingDefinitions?.data);
        }

        if (contentTypes?.data) {
          const formatContentType = contentTypes?.data?.items?.filter((type: any) => type?.id !== 0)?.sort((a: any, b: any) => a.ordering - b.ordering);
          setDataContentType(formatContentType?.map((type: any) => ({
            value: type?.id,
            label: type?.name
          })));
        }
      } catch (error: any) {
        return error;
      }
    };
    handleCallAllOption();
  }, []);

  useEffect(() => {
    document.title = `${t("Community")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Community")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-5 con-sm-6">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={contents?.pagination?.total || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                    <div className="col-7 con-sm-6 text-end">
                      {isHavePermissionRole(
                        ROLES_FOR_APP.CONTENT_MANAGEMENT_CREATE,
                        userPermissions
                      ) && (
                          <div className="col-sm-auto">
                            <div>
                              <button
                                type="button"
                                className="btn btn-success add-btn"
                                onClick={() => {
                                  setIsOpen(true);
                                  setItemToEdit(null);
                                }}
                              >
                                <i className="ri-add-line align-bottom me-1"></i>
                                {t("Create Content")}
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  </Row>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 ">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          isShow={!!nickNameSearch}
                          label={`${t("Nick Name")}`}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Nick Name")}...`}
                            value={nickNameSearch}
                            onChange={(e) => setNickNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Email")} isShow={!!emailSearch}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email")}...`}
                            value={emailSearch}
                            onChange={(e) => setEmailSearch(e.target.value)}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          isShow={!!storeNameSearch}
                          label={`${t("Store Name")}`}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Store Name")}...`}
                            value={storeNameSearch}
                            onChange={(e) => setStoreNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={`${t("Content")}`}
                          isShow={!!contentSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Content")}...`}
                            value={contentSearch}
                            onChange={(e) => setContentSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Content Type")} isShow={true}>
                          <DropdownStatusNew
                            name="reservation_type"
                            dataList={dataContentType || []}
                            placeholder={`${t("Content Type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={contentTypeSearch}
                            onChangeSelect={(event: any) =>
                              setContentTypeSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Created Type")} isShow={true}>
                          <DropdownStatusNew
                            name="reservation_type"
                            dataList={LIST_CONTENT_CREATED_TYPE_OPTION || []}
                            placeholder={`${t("Created Type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={createdTypeSearch}
                            onChangeSelect={(event: any) =>
                              setCreatedTypeSearch(event?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_CONTENT_CREATED_TYPE}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Status")} isShow={true}>
                          <DropdownStatusNew
                            name="status"
                            dataList={LIST_CONTENT_STATUS_OPTION || []}
                            placeholder={`${t("Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(e: any) =>
                              setStatusSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_CONTENT_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-sm-4 mt-md-3 mt-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={12}
                        lg={12}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed sticky-table-content-post-media"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={contents?.items?.length ? contents?.items : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={contents?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(contents?.pagination?.total) /
                        Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isContentLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {isOpen && (
          <ContentForm
            id={itemToEdit?.id}
            item={itemToEdit}
            dataRatingDefinition={dataRatingDefinition}
            dataContentType={dataContentType}
            onCloseClick={onCloseClick}
            triggerRefresh={triggerRefresh}
            setLinkImage={setLinkImage}
          />
        )}

        {itemToDelete && (
          <ModalConfirm
            header={t("Delete This Content")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        <ModalPreview
          type={linkImage?.isVideo ? "VIDEO" : "IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreviewImage
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          onClose={() => onCloseImageClick()}
        />

        {itemToCommentList && (
          <CommentDetailModal
            item={itemToCommentList}
            onClose={onCloseCommentListClick}
            triggerRefresh={triggerRefresh}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default ContentList;
